import { DEFAULT_CURRENCY_CODE, computeLineAmount, type LineDiscount } from '../money';
import { convertQuantity, correctedQuantity, type QuantityCorrectionReason, type UnitLike } from '../catalog/catalog';
import { RefusalError } from '../failures/refusal';

/**
 * Item Table (piece 4, decision 0181): the shared line-item grid takes a column
 * set from the page that hosts it and has no sales-or-purchase mode. One
 * registry row per document type lists its default columns, marks the six
 * locked ones, and names where a new line's unitPrice seeds from and what the
 * reference line compares it against. The registry holds declarative keys;
 * the hosting page resolves them.
 */
export type DocumentType = 'Quote' | 'SalesOrder' | 'Invoice' | 'PurchaseOrder' | 'RFQ' | 'Bill' | 'CreditNote' | 'ChangeOrder' | 'BOM' | 'WorkOrder';

export const documentTypes: readonly DocumentType[] = ['Quote', 'SalesOrder', 'Invoice', 'PurchaseOrder', 'RFQ', 'Bill', 'CreditNote', 'ChangeOrder', 'BOM', 'WorkOrder'];

/** Item, Description, Qty, Unit, Rate and Amount are locked in every set. */
export const LOCKED_COLUMNS = ['item', 'description', 'qty', 'unit', 'rate', 'amount'] as const;
export type LockedColumn = (typeof LOCKED_COLUMNS)[number];

export type ItemTableColumn = LockedColumn | 'account' | 'tax' | 'discount' | string;

/** Where a fresh line's unitPrice comes from, as a declarative key the document resolves. */
export type PriceSeed =
  | { kind: 'priceSheetOrItem'; itemField: 'salesPrice' }
  | { kind: 'sourceLineOrFresh'; source: 'Quote' | 'SalesOrder' | 'Invoice' | 'PurchaseOrder'; fresh: PriceSeed }
  | { kind: 'rfqResponseOrItem'; itemField: 'purchasePrice' }
  | { kind: 'sourceLine'; source: 'PurchaseOrder' | 'Invoice' }
  | { kind: 'blank' }
  | { kind: 'snapshot'; itemField: 'purchasePrice'; writesTo: 'BOMLine.purchasePrice' };

export type PriceReference = 'Item.listPrice' | 'Item.purchasePrice' | 'PurchaseOrder.line' | null;

export interface RegistryRow {
  documentType: DocumentType;
  defaultColumns: readonly ItemTableColumn[];
  locked: readonly LockedColumn[];
  seed: PriceSeed;
  reference: PriceReference;
  /** Sales documents draw an under-catalog rate grey; purchase documents draw it amber. */
  side: 'sales' | 'purchase' | 'none';
  /**
   * 🔨 Whether a line may name no catalog Item (decision 0649). The BOM's lines
   * each name an Item, so its footer offers no free-text row; every document
   * does. A document's rule, kept in the registry, never a second table.
   */
  freeTextRows: boolean;
}

const quoteSeed: PriceSeed = { kind: 'priceSheetOrItem', itemField: 'salesPrice' };

export const itemTableRegistry: Readonly<Record<DocumentType, RegistryRow>> = {
  // 🔒 Every priced document draws the account and the category, so all four resolve alike (decisions 0097, 0435).
  Quote: { documentType: 'Quote', defaultColumns: [...LOCKED_COLUMNS, 'account', 'tax'], locked: LOCKED_COLUMNS, seed: quoteSeed, reference: 'Item.listPrice', side: 'sales', freeTextRows: true },
  SalesOrder: { documentType: 'SalesOrder', defaultColumns: [...LOCKED_COLUMNS, 'account', 'tax'], locked: LOCKED_COLUMNS, seed: { kind: 'sourceLineOrFresh', source: 'Quote', fresh: quoteSeed }, reference: 'Item.listPrice', side: 'sales', freeTextRows: true },
  Invoice: { documentType: 'Invoice', defaultColumns: [...LOCKED_COLUMNS, 'account', 'tax'], locked: LOCKED_COLUMNS, seed: { kind: 'sourceLineOrFresh', source: 'SalesOrder', fresh: quoteSeed }, reference: 'Item.listPrice', side: 'sales', freeTextRows: true },
  // 🔨 The Purchase Order draws the tax category beside the account, and no tax amount: WEA is the buyer, and what a vendor charges reaches the books on the Bill (decision 0435).
  PurchaseOrder: { documentType: 'PurchaseOrder', defaultColumns: [...LOCKED_COLUMNS, 'account', 'tax'], locked: LOCKED_COLUMNS, seed: { kind: 'rfqResponseOrItem', itemField: 'purchasePrice' }, reference: 'Item.purchasePrice', side: 'purchase', freeTextRows: true },
  RFQ: { documentType: 'RFQ', defaultColumns: [...LOCKED_COLUMNS], locked: LOCKED_COLUMNS, seed: { kind: 'blank' }, reference: null, side: 'purchase', freeTextRows: true },
  Bill: { documentType: 'Bill', defaultColumns: [...LOCKED_COLUMNS, 'account'], locked: LOCKED_COLUMNS, seed: { kind: 'sourceLine', source: 'PurchaseOrder' }, reference: 'PurchaseOrder.line', side: 'purchase', freeTextRows: true },
  CreditNote: { documentType: 'CreditNote', defaultColumns: [...LOCKED_COLUMNS, 'account', 'tax'], locked: LOCKED_COLUMNS, seed: { kind: 'sourceLine', source: 'Invoice' }, reference: 'Item.listPrice', side: 'sales', freeTextRows: true },
  ChangeOrder: { documentType: 'ChangeOrder', defaultColumns: [...LOCKED_COLUMNS, 'tax'], locked: LOCKED_COLUMNS, seed: { kind: 'sourceLineOrFresh', source: 'SalesOrder', fresh: quoteSeed }, reference: 'Item.listPrice', side: 'sales', freeTextRows: true },
  BOM: { documentType: 'BOM', defaultColumns: [...LOCKED_COLUMNS], locked: LOCKED_COLUMNS, seed: { kind: 'snapshot', itemField: 'purchasePrice', writesTo: 'BOMLine.purchasePrice' }, reference: null, side: 'none', freeTextRows: false },
  // 🔒 Piece 29, review item T141: the Work Order's labour lines on the one grid. A line is its description, priced by the workers on it, so nothing seeds a rate.
  WorkOrder: { documentType: 'WorkOrder', defaultColumns: [...LOCKED_COLUMNS], locked: LOCKED_COLUMNS, seed: { kind: 'blank' }, reference: null, side: 'none', freeTextRows: true },
};

export function isLockedColumn(column: string): column is LockedColumn {
  return (LOCKED_COLUMNS as readonly string[]).includes(column);
}

export class LockedColumnError extends RefusalError {
  constructor(column: string) {
    super(`${column} is locked in every Item Table column set (decision 0181)`);
    this.name = 'LockedColumnError';
  }
}

/** The column picker refuses to remove a locked column at the picker, not at save. */
export function removeColumn(columns: readonly ItemTableColumn[], column: string): ItemTableColumn[] {
  if (isLockedColumn(column)) throw new LockedColumnError(column);
  return columns.filter((c) => c !== column);
}

export function addColumn(columns: readonly ItemTableColumn[], column: ItemTableColumn): ItemTableColumn[] {
  return columns.includes(column) ? [...columns] : [...columns, column];
}

/** The picker offers any field the line-item model carries, never the print projection. */
export function pickableColumns(lineFields: readonly string[], printProjection: readonly string[] = []): string[] {
  const print = new Set(printProjection);
  return lineFields.filter((f) => !print.has(`print:${f}`));
}

export type DiscountLevel = 'Line' | 'Footer';

/** The Discount column exists on screen only when documents.discountLevel is Line; Footer removes it entirely. */
export function discountColumnShown(level: DiscountLevel): boolean {
  return level === 'Line';
}

/** Amount is qty × rate, less the line discount when the column is on, in integer cents; never edited directly. */
export function lineAmountCents(qty: number, rateCents: number, discount?: LineDiscount, currencyCode = DEFAULT_CURRENCY_CODE): number {
  return computeLineAmount(qty, { amount: rateCents, currencyCode }, discount).amount;
}

export type ReferenceTone = 'grey' | 'amber' | null;

/** The reference line: grey on a sales document, amber on a purchase document, only when the rate is under the catalog price. */
export function referenceTone(row: RegistryRow, rateCents: number, referenceCents: number | null): ReferenceTone {
  if (referenceCents === null || rateCents >= referenceCents) return null;
  if (row.side === 'sales') return 'grey';
  if (row.side === 'purchase') return 'amber';
  return null;
}

/** The per-user preference key: person and document type, with no organisation dimension (decision 0015). */
export function columnSetScopeKey(documentType: DocumentType): string {
  return `itemTable.${documentType}`;
}

// ---- The line's unit: a closed list, never typed (decision 0439) ----

/** One approved unit a document line can carry: a `Unit` row carrying `isSaleUnit` (piece 6). */
export interface ApprovedUnit {
  id: string;
  unitName: string;
  abbreviation: string;
  familyName: string;
  /** 🔒 The `Unit` row's own conversion into its family's base, which the sale-quantity correction converts by (decision 1526); absent on a list that never carried it. */
  conversionFactor?: number;
  conversionOffset?: number;
  allowsFractionalQty?: boolean;
}

/** What the unit cell reads: the unit name with its abbreviation, so Each and ea are one row. */
export function approvedUnitLabel(unit: ApprovedUnit): string {
  return `${unit.unitName} (${unit.abbreviation})`;
}

const sameUnit = (unit: ApprovedUnit, text: string): boolean => {
  const wanted = text.trim().toLowerCase();
  return wanted.length > 0 && (unit.unitName.toLowerCase() === wanted || unit.abbreviation.toLowerCase() === wanted);
};

/** The approved unit a line is standing on, matched by name or abbreviation; null where the line carries one nobody approved. */
export function approvedUnitOf(units: readonly ApprovedUnit[], currentUnit: string): ApprovedUnit | null {
  return units.find((unit) => sameUnit(unit, currentUnit)) ?? null;
}

/**
 * 🔨 The units a line offers (decision 0439). A write-in line offers every
 * approved unit, because it names no Item and so has no family to respect. A
 * line naming an Item offers the approved units of that Item's own family,
 * which is the rule the document services already refuse a save against. A
 * line whose unit nobody approved offers none, so the cell stays locked rather
 * than proposing a change that would be refused.
 *
 * 🔨 A line naming an Item whose unit is blank offers none either. The blank is
 * a unit the row was built without, never a free choice: the Sales-Order-derived
 * invoice rows arrive that way, and offering every family there drew a pencil
 * onto a length that would take hours (found in review, 2026-09-16).
 */
export function approvedUnitsFor(units: readonly ApprovedUnit[], line: { freeText: boolean; unit: string }): readonly ApprovedUnit[] {
  if (line.freeText) return units;
  if (line.unit.trim().length === 0) return [];
  const standing = approvedUnitOf(units, line.unit);
  if (!standing) return [];
  return units.filter((unit) => unit.familyName === standing.familyName);
}

/**
 * 🔨 Whether the cell offers a change at all. One unit in the family is no
 * choice, and an override that offers the unit already showing is a control
 * that does nothing.
 */
export function unitOverrideOffered(units: readonly ApprovedUnit[], line: { freeText: boolean; unit: string }): boolean {
  return !line.freeText && approvedUnitsFor(units, line).length > 1;
}

// ---- The sale guards, applied by the table before the refusal (decision 1526) ----

/** 🔒 What the correction reads for one Item: its minimum and its increment, both in its own unit, and that unit as the line names units. */
export interface SaleQuantityRule {
  minimum: number | null;
  increment: number | null;
  /** The Item's own unit, by name or abbreviation, matched against the approved units as a line's unit is. */
  unit: string;
}

/** A quantity the table moved: the number the person typed, why it moved, and the rule it moved by. */
export interface LineQuantityCorrection {
  quantity: number;
  from: number;
  reason: QuantityCorrectionReason;
}

/** The one unit both sides stand on when the line reads the Item's own unit, so no conversion runs. */
const SAME_UNIT: UnitLike = { id: 'same', familyId: 'same', conversionFactor: 1, conversionOffset: 0, allowsFractionalQty: true };

/** An approved unit as the conversion reads it, or null where the list never carried its factor. */
export function unitLikeOf(unit: ApprovedUnit | null): UnitLike | null {
  if (!unit || typeof unit.conversionFactor !== 'number' || unit.conversionFactor === 0) return null;
  return { id: unit.id, familyId: unit.familyName, conversionFactor: unit.conversionFactor, conversionOffset: unit.conversionOffset ?? 0, allowsFractionalQty: unit.allowsFractionalQty ?? true };
}

/**
 * The line's unit and the Item's, as the conversion reads them. A line on the
 * Item's own unit, or on none, converts nothing. Otherwise both units resolve
 * through the approved list with their factors, in one family; where either
 * does not, the table corrects nothing and the server's refusal answers.
 */
function ruleUnits(units: readonly ApprovedUnit[], lineUnit: string, itemUnit: string): [UnitLike, UnitLike] | null {
  const line = lineUnit.trim();
  const item = itemUnit.trim();
  if (!line || line.toLowerCase() === item.toLowerCase()) return [SAME_UNIT, SAME_UNIT];
  const fromApproved = approvedUnitOf(units, line);
  const toApproved = approvedUnitOf(units, item);
  if (fromApproved && toApproved && fromApproved.id === toApproved.id) return [SAME_UNIT, SAME_UNIT];
  const from = unitLikeOf(fromApproved);
  const to = unitLikeOf(toApproved);
  if (!from || !to || from.familyId !== to.familyId) return null;
  return [from, to];
}

/**
 * 🔒 The committed quantity through the Item's sale guards (decision 1526):
 * null where nothing moved or the rule cannot be read, the corrected
 * quantity in the line's unit with the typed number otherwise.
 */
export function lineQuantityCorrection(quantity: number | null, lineUnit: string, rule: SaleQuantityRule | null, units: readonly ApprovedUnit[]): LineQuantityCorrection | null {
  if (quantity === null || !rule || (rule.minimum === null && rule.increment === null)) return null;
  const pair = ruleUnits(units, lineUnit, rule.unit);
  if (!pair) return null;
  const answer = correctedQuantity(quantity, pair[0], pair[1], { minimumSaleQuantity: rule.minimum, saleQuantityIncrement: rule.increment });
  return answer.moved && answer.reason ? { quantity: answer.quantity, from: quantity, reason: answer.reason } : null;
}

/** 🔒 The quantity a pick seeds onto an untyped line (decision 1526): the Item's minimum in the line's unit, or null where it has none or the units do not resolve. */
export function seededMinimum(lineUnit: string, rule: SaleQuantityRule | null, units: readonly ApprovedUnit[]): number | null {
  if (!rule || rule.minimum === null || rule.minimum <= 0) return null;
  const pair = ruleUnits(units, lineUnit, rule.unit);
  if (!pair) return null;
  return Math.round(convertQuantity(rule.minimum, pair[1], pair[0]) * 1e9) / 1e9;
}

// ---- Group dividers on every document (decision 0650) ----

/**
 * 🔒 A named group divider in a document's lines (decisions 0333, 0650): the
 * lines beneath it, down to the next divider, are its group. `position` is its
 * 1-based place in the document's one reading order, lines and dividers
 * together; the lines fill every place no divider holds, in their own order.
 */
export interface DividerPlace {
  name: string;
  position: number;
}

/** A stored divider, as a document's read answers it. */
export interface DividerRow extends DividerPlace {
  id: string;
}

/** One row of a document's reading order: a line, or a divider's name. */
export type ItemTableRow<L> = { kind: 'line'; line: L } | { kind: 'divider'; name: string };

/**
 * The reading order rebuilt from what is stored: each divider at its place,
 * and the lines filling every other place in their own order. 🔨 A divider
 * whose place lies past the last row stands at the end rather than vanishing,
 * so a stored row the arithmetic did not expect is still drawn.
 */
export function interleaveDividers<L>(lines: readonly L[], dividers: readonly DividerPlace[]): ItemTableRow<L>[] {
  const sorted = [...dividers].sort((a, b) => a.position - b.position);
  const out: ItemTableRow<L>[] = [];
  let line = 0;
  let divider = 0;
  const total = lines.length + sorted.length;
  for (let place = 1; place <= total; place += 1) {
    if (divider < sorted.length && sorted[divider]!.position <= place) {
      out.push({ kind: 'divider', name: sorted[divider]!.name });
      divider += 1;
    } else if (line < lines.length) {
      out.push({ kind: 'line', line: lines[line]! });
      line += 1;
    }
  }
  for (; divider < sorted.length; divider += 1) out.push({ kind: 'divider', name: sorted[divider]!.name });
  for (; line < lines.length; line += 1) out.push({ kind: 'line', line: lines[line]! });
  return out;
}

/** The reading order as a save sends it: the lines in their order, and each divider at its place among all the rows. */
export function dividerPlaces<L>(rows: readonly ItemTableRow<L>[]): { lines: L[]; dividers: DividerPlace[] } {
  const lines: L[] = [];
  const dividers: DividerPlace[] = [];
  rows.forEach((row, index) => {
    if (row.kind === 'line') lines.push(row.line);
    else dividers.push({ name: row.name, position: index + 1 });
  });
  return { lines, dividers };
}

/**
 * 🔒 The dividers a converted document carries (decision 0650): the lines the
 * new document takes, each divider kept above them when its group keeps at
 * least one line, and a divider whose group keeps none dropped. A Quote's
 * groups reach the Sales Order and the Invoice this way, and a Purchase
 * Order's reach its Bill.
 */
export function carryDividers<L>(rows: readonly ItemTableRow<L>[], kept: (line: L) => boolean): ItemTableRow<L>[] {
  const out: ItemTableRow<L>[] = [];
  let pending: ItemTableRow<L> | null = null;
  for (const row of rows) {
    if (row.kind === 'divider') {
      pending = row;
      continue;
    }
    if (!kept(row.line)) continue;
    if (pending) {
      out.push(pending);
      pending = null;
    }
    out.push(row);
  }
  return out;
}

/** What a save refuses in the dividers it is sent: a divider with no name, or one standing outside the document's rows. */
export function dividerProblem(dividers: readonly DividerPlace[] | null | undefined, lineCount: number): string | null {
  const list = dividers ?? [];
  const total = lineCount + list.length;
  const seen = new Set<number>();
  for (const divider of list) {
    if (!(divider.name ?? '').trim()) return 'every group divider carries a name';
    if (!Number.isInteger(divider.position) || divider.position < 1 || divider.position > total) return `a group divider stands at place ${divider.position}, outside the ${total} rows of the document`;
    if (seen.has(divider.position)) return `two group dividers stand at place ${divider.position}`;
    seen.add(divider.position);
  }
  return null;
}

// ---- The width budget (decision 0651) ----

/**
 * 🔒 The Item Table's width budget, in rem (decision 0651; product owner,
 * 2026-09-22: "item table formatting is not great it can be optimized to fill
 * space better"). Each column is as wide as its widest legal value and no
 * wider: the quantity over its unit, a five-figure rate with its sign, a
 * six-figure amount. The Item column takes whatever the table has beyond them,
 * never less than its floor. The stylesheet's tokens carry the same numbers,
 * and a web test holds the two together.
 */
export const ITEM_TABLE_BUDGET_REM = {
  handle: 3,
  item: 11,
  qty: 6,
  // 🔒 Decision 0860: Budget (hours) is a numeric cell beside the quantity, the width every other picker-added column takes.
  budget: 8,
  rate: 10,
  amount: 7.5,
  account: 11,
  tax: 10.5,
  discount: 10,
  other: 8,
  remove: 2.5,
} as const;

/**
 * 🔒 The order the pickers give way in as the pane narrows (decision 0651): each
 * folds out of its column into the item cell, beneath the description, until
 * the table fits. Account goes first, because it posts behind the scenes and a
 * rep rarely changes it; Tax next; the Discount last, because it moves the
 * amount a customer reads. The quantity, the rate and the amount never fold.
 */
// 🔒 Decision 0860: Budget (hours) folds last, beneath the description on a service line, so a narrow pane keeps the
// width it had before the column joined.
export const ITEM_TABLE_FOLD_ORDER = ['account', 'tax', 'discount', 'budget'] as const;

/** The width one column asks for, in rem; the Item column asks for its floor. */
export function itemTableColumnRem(column: string): number {
  const budget = ITEM_TABLE_BUDGET_REM as Readonly<Record<string, number>>;
  return budget[column] ?? ITEM_TABLE_BUDGET_REM.other;
}

export interface ItemTableLayout {
  /** The columns drawn as columns, in the host's order. */
  drawn: string[];
  /** The columns folded into the item cell, in the host's order. */
  folded: string[];
  /** The narrowest the table draws before its box scrolls, in rem: every drawn column and the Item column's floor. */
  minRem: number;
}

/**
 * 🔒 Which columns stand as columns in a box `boxRem` wide (decision 0651). A
 * picker folds, in the fold order, while the drawn columns and the Item floor
 * are wider than the box. 🔨 A box nobody has measured folds nothing, so a grid
 * drawn before its first measure, or outside a browser, is the full table.
 */
export function itemTableLayout(columns: readonly string[], boxRem: number | null, chrome: { handle: boolean; remove: boolean }): ItemTableLayout {
  const extra = (chrome.handle ? ITEM_TABLE_BUDGET_REM.handle : 0) + (chrome.remove ? ITEM_TABLE_BUDGET_REM.remove : 0);
  const width = (drawn: readonly string[]) => extra + drawn.reduce((sum, column) => sum + itemTableColumnRem(column), 0);
  let drawn = [...columns];
  const folded = new Set<string>();
  if (boxRem !== null) {
    for (const column of ITEM_TABLE_FOLD_ORDER) {
      if (width(drawn) <= boxRem) break;
      if (!drawn.includes(column)) continue;
      folded.add(column);
      drawn = drawn.filter((c) => c !== column);
    }
  }
  return { drawn, folded: columns.filter((c) => folded.has(c)), minRem: width(drawn) };
}
